import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatCLP, formatUSD, resolveImageUrl } from "../../lib/format";
import { extractYouTubeId, youtubeThumbnail } from "../../lib/youtube";

const emptyProduct = {
    name: "", slug: "", short_description: "", full_description: "",
    price_clp: 0, price_usd: 0, sale_price_clp: 0, sale_price_usd: 0, image_url: "",
    cyberday_included: false, cyberday_discount_pct: 0,
    formats: ["VST3", "AU"], operating_systems: ["Windows", "macOS"],
    status: "draft", current_version: "1.0.0", release_label: "",
    features: [], system_requirements: [], youtube_videos: [], category_ids: [],
    trial_days: "",  // "" = use global default (5 days); admin can override per product
};

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        api.get("/admin/categories").then(({ data }) => setCategories(data || [])).catch(() => {});
    }, []);

    const toggleCategory = (id) => {
        setForm((f) => {
            const cur = f.category_ids || [];
            return { ...f, category_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
        });
    };

    const load = async () => {
        const { data } = await api.get("/admin/products", { params: q ? { q } : {} });
        setProducts(data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

    const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    const handleImageUpload = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploadingImage(true);
        try {
            const { data } = await api.post("/admin/products/upload-image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, image_url: data.url }));
            toast.success("Image uploaded successfully.");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setUploadingImage(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                price_clp: Number(form.price_clp) || 0,
                price_usd: Number(form.price_usd) || 0,
                sale_price_clp: Number(form.sale_price_clp) || 0,
                sale_price_usd: Number(form.sale_price_usd) || 0,
                cyberday_included: !!form.cyberday_included,
                cyberday_discount_pct: Math.max(0, Math.min(100, Number(form.cyberday_discount_pct) || 0)),
                features: typeof form.features === "string" ? form.features.split("\n").filter(Boolean) : form.features,
                system_requirements: typeof form.system_requirements === "string" ? form.system_requirements.split("\n").filter(Boolean) : form.system_requirements,
                youtube_videos: (typeof form.youtube_videos === "string"
                    ? form.youtube_videos.split("\n")
                    : (form.youtube_videos || [])
                ).map((s) => extractYouTubeId(s)).filter(Boolean),
                trial_days: (form.trial_days === "" || form.trial_days == null)
                    ? null
                    : Math.max(1, Math.min(365, Number(form.trial_days))),
                category_ids: form.category_ids || [],
            };
            delete payload.categories;
            if (editing) {
                await api.patch(`/admin/products/${editing.id}`, payload);
                toast.success("Product updated successfully.");
                setOpen(false); setEditing(null); setForm(emptyProduct);
            } else {
                const { data: created } = await api.post("/admin/products", payload);
                toast.success(`Product created · UUID: ${created.id.slice(0, 8)}…`);
                // Re-open in edit mode so user can immediately copy the UUID / #define snippet
                setEditing(created);
                setForm({
                    ...created,
                    features: (created.features || []).join("\n"),
                    system_requirements: (created.system_requirements || []).join("\n"),
                    youtube_videos: (created.youtube_videos || []).map((id) => `https://youtu.be/${id}`).join("\n"),
                    trial_days: created.trial_days ?? "",
                });
            }
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            ...p,
            features: (p.features || []).join("\n"),
            system_requirements: (p.system_requirements || []).join("\n"),
            youtube_videos: (p.youtube_videos || []).map((id) => `https://youtu.be/${id}`).join("\n"),
            trial_days: p.trial_days ?? "",
        });
        setOpen(true);
    };

    const openCreate = () => { setEditing(null); setForm(emptyProduct); setOpen(true); };

    const remove = async (p) => {
        if (!window.confirm(`Delete plugin "${p.name}"?`)) return;
        try {
            await api.delete(`/admin/products/${p.id}`);
            toast.success("Plugin deleted");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch {
            // Fallback for older browsers / non-secure contexts
            const ta = document.createElement("textarea");
            ta.value = text; document.body.appendChild(ta); ta.select();
            try { document.execCommand("copy"); toast.success(`${label} copied`); }
            catch { toast.error("Could not copy — copy it manually"); }
            finally { document.body.removeChild(ta); }
        }
    };

    const cppSnippet = (p) =>
`// ${p.name}
#define AVX_PRODUCT_ID  "${p.id}"
#define AVX_BACKEND_URL "https://avx-studio-hub.emergent.host"
#include "AVX/AVXLicense.h"`;

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-products-page">
                <div>
                    <div className="label-mono mb-3">/ Product management</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Products</h1>
                </div>
                <button onClick={openCreate} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="create-product-btn">
                    + Create product
                </button>
            </div>

            <div className="mb-4">
                <Input placeholder="Search by name or slug…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-avx-surface border-avx-line h-11 max-w-md" data-testid="products-search" />
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {products.map((p) => (
                    <div key={p.id} className="panel-elevated p-5" data-testid={`product-card-${p.id}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <h3 className="font-heading font-bold text-lg leading-tight">{p.name}</h3>
                                <div className="font-mono text-[10px] text-avx-muted uppercase tracking-[0.18em] mt-1">{p.slug}</div>
                            </div>
                            <StatusBadge status={p.status} />
                        </div>
                        {p.image_url && <img src={resolveImageUrl(p.image_url)} alt="" className="w-full aspect-[16/9] object-cover rounded-sm border border-avx-line mb-3" />}
                        <p className="text-xs text-avx-muted line-clamp-2 leading-relaxed mb-3">{p.short_description}</p>
                        <div className="flex items-end justify-between mb-3">
                            <div>
                                {(() => {
                                    const salePct = p.sale_price_clp > 0 && p.sale_price_clp < p.price_clp
                                        ? Math.round(((p.price_clp - p.sale_price_clp) / p.price_clp) * 100)
                                        : 0;
                                    if (salePct > 0) {
                                        return (
                                            <>
                                                <div className="flex items-baseline gap-2">
                                                    <div className="font-mono text-avx-muted text-xs line-through">{formatCLP(p.price_clp)}</div>
                                                    <div className="font-mono text-avx-danger font-semibold">{formatCLP(p.sale_price_clp)}</div>
                                                    <span className="text-[9px] font-mono font-bold text-white bg-avx-danger px-1.5 py-0.5 rounded-sm">−{salePct}%</span>
                                                </div>
                                                <div className="font-mono text-xs text-avx-muted">{formatUSD(p.sale_price_usd || p.price_usd)}</div>
                                            </>
                                        );
                                    }
                                    return (
                                        <>
                                            <div className="font-mono text-avx-blue font-semibold">{formatCLP(p.price_clp)}</div>
                                            <div className="font-mono text-xs text-avx-muted">{formatUSD(p.price_usd)}</div>
                                        </>
                                    );
                                })()}
                            </div>
                            <div className="label-mono">v{p.current_version}</div>
                        </div>
                        <div className="mb-3 p-2.5 bg-avx-bg/60 border border-avx-line rounded-sm" data-testid={`product-uuid-${p.id}`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="label-mono text-[9px]">AVX_PRODUCT_ID</span>
                                <div className="flex gap-1">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); copyToClipboard(p.id, "UUID"); }}
                                        className="text-[9px] font-mono uppercase tracking-[0.16em] text-avx-blue hover:text-avx-blue/80 px-1.5 py-0.5 rounded-sm hover:bg-avx-blue/10"
                                        data-testid={`copy-uuid-${p.id}`}
                                        title="Copy UUID"
                                    >
                                        Copy UUID
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); copyToClipboard(cppSnippet(p), "Snippet C++"); }}
                                        className="text-[9px] font-mono uppercase tracking-[0.16em] text-avx-blue hover:text-avx-blue/80 px-1.5 py-0.5 rounded-sm hover:bg-avx-blue/10"
                                        data-testid={`copy-snippet-${p.id}`}
                                        title="Copy snippet ready for PluginProcessor.h"
                                    >
                                        Copy #define
                                    </button>
                                </div>
                            </div>
                            <code className="font-mono text-[10px] text-avx-text/80 break-all select-all">{p.id}</code>
                        </div>
                        <div className="flex gap-2 pt-3 border-t border-avx-line">
                            <button onClick={() => openEdit(p)} className="flex-1 btn-secondary py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm" data-testid={`edit-product-${p.id}`}>Edit</button>
                            <button onClick={() => remove(p)} className="px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] text-avx-danger hover:bg-avx-danger/10 rounded-sm border border-avx-danger/30">Delete</button>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit product" : "Create product"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="product-form">
                        {editing && (
                            <div className="p-3 bg-avx-blue/5 border border-avx-blue/30 rounded-sm" data-testid="editing-uuid-block">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="label-mono text-avx-blue">/ Product ID (for the C++ plugin)</span>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => copyToClipboard(editing.id, "UUID")} className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline" data-testid="modal-copy-uuid">
                                            Copy UUID
                                        </button>
                                        <button type="button" onClick={() => copyToClipboard(cppSnippet(editing), "Snippet C++")} className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline" data-testid="modal-copy-snippet">
                                            Copy #define
                                        </button>
                                    </div>
                                </div>
                                <code className="font-mono text-[11px] text-avx-text/90 break-all select-all block mb-2">{editing.id}</code>
                                <pre className="bg-avx-bg/80 border border-avx-line rounded-sm p-2 text-[10px] font-mono text-avx-text/80 overflow-x-auto">{cppSnippet(editing)}</pre>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Name</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.name} onChange={(e) => {
                                    const name = e.target.value;
                                    setForm({ ...form, name, slug: editing ? form.slug : slugify(name) });
                                }} required data-testid="product-name" />
                            </div>
                            <div>
                                <Label className="label-mono">Slug</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} required data-testid="product-slug" />
                            </div>
                        </div>
                        <div>
                            <Label className="label-mono">Short description</Label>
                            <Textarea rows={2} className="mt-2 bg-avx-bg border-avx-line" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
                        </div>
                        <div>
                            <Label className="label-mono">Full description</Label>
                            <Textarea rows={4} className="mt-2 bg-avx-bg border-avx-line" value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} />
                        </div>
                        <div data-testid="product-categories">
                            <Label className="label-mono">Categorías</Label>
                            {categories.length === 0 ? (
                                <p className="text-[11px] font-mono text-avx-muted mt-2">No hay categorías. Créalas en <span className="text-avx-blue">Categorías</span>.</p>
                            ) : (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {categories.map((c) => {
                                        const on = (form.category_ids || []).includes(c.id);
                                        return (
                                            <button
                                                type="button"
                                                key={c.id}
                                                onClick={() => toggleCategory(c.id)}
                                                data-testid={`product-category-${c.slug}`}
                                                className={`px-3 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-[0.15em] border transition-colors ${on ? "border-avx-blue bg-avx-blue/15 text-avx-blue" : "border-avx-line text-avx-muted hover:border-avx-blue/40"}`}
                                            >
                                                {on ? "✓ " : ""}{c.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Price CLP</Label>
                                <Input type="number" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.price_clp} onChange={(e) => setForm({ ...form, price_clp: e.target.value })} data-testid="product-price-clp" />
                            </div>
                            <div>
                                <Label className="label-mono">Price USD</Label>
                                <Input type="number" step="0.01" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} data-testid="product-price-usd" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono flex items-center gap-2">
                                    Sale price CLP
                                    <span className="text-avx-muted normal-case tracking-normal text-[10px]">(0 = no sale)</span>
                                </Label>
                                <Input type="number" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.sale_price_clp} onChange={(e) => setForm({ ...form, sale_price_clp: e.target.value })} placeholder="0" data-testid="product-sale-clp" />
                            </div>
                            <div>
                                <Label className="label-mono flex items-center gap-2">
                                    Sale price USD
                                    <span className="text-avx-muted normal-case tracking-normal text-[10px]">(0 = no sale)</span>
                                </Label>
                                <Input type="number" step="0.01" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.sale_price_usd} onChange={(e) => setForm({ ...form, sale_price_usd: e.target.value })} placeholder="0" data-testid="product-sale-usd" />
                            </div>
                        </div>
                        {Number(form.sale_price_clp) > 0 && Number(form.sale_price_clp) < Number(form.price_clp) && (
                            <div className="panel p-3 bg-avx-danger/8 border-avx-danger/40 text-xs">
                                <span className="font-heading font-bold text-avx-danger">
                                    Active sale: −{Math.round(((form.price_clp - form.sale_price_clp) / form.price_clp) * 100)}%
                                </span>
                                <span className="text-avx-muted ml-2">
                                    · A red badge will be shown in the catalog and a crossed-out price on the plugin page.
                                </span>
                            </div>
                        )}

                        {/* CyberDay auto-discount */}
                        <div
                            className="panel p-4 space-y-3"
                            style={form.cyberday_included ? { background: "linear-gradient(90deg, rgba(255,46,154,0.10), rgba(124,58,237,0.10), rgba(34,211,238,0.08))" } : undefined}
                            data-testid="product-cyberday-block"
                        >
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!form.cyberday_included}
                                    onChange={(e) => setForm({ ...form, cyberday_included: e.target.checked })}
                                    className="accent-[#7C3AED]"
                                    data-testid="product-cyberday-included"
                                />
                                <span className="font-heading font-bold">Include in CyberDay</span>
                                <span className="text-avx-muted text-[10px] font-mono">(auto-discount only while the event is live)</span>
                            </label>
                            {form.cyberday_included && (
                                <>
                                    <div className="grid grid-cols-2 gap-3 items-end">
                                        <div>
                                            <Label className="label-mono">CyberDay discount %</Label>
                                            <Input
                                                type="number" min="1" max="100"
                                                className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"
                                                value={form.cyberday_discount_pct}
                                                onChange={(e) => setForm({ ...form, cyberday_discount_pct: e.target.value })}
                                                placeholder="30"
                                                data-testid="product-cyberday-pct"
                                            />
                                        </div>
                                        {Number(form.cyberday_discount_pct) > 0 && Number(form.price_clp) > 0 && (
                                            <div className="text-xs font-mono">
                                                <div className="text-avx-muted line-through">{formatCLP(form.price_clp)}</div>
                                                <div className="font-heading font-bold text-base" style={{ color: "#FF2E9A" }}>
                                                    {formatCLP(Math.round(Number(form.price_clp) * (1 - Number(form.cyberday_discount_pct) / 100)))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-avx-muted font-mono">
                                        CyberDay overrides any sale price while the event is live, and reverts automatically when it ends.
                                    </p>
                                </>
                            )}
                        </div>

                        <div>
                            <Label className="label-mono">Plugin image</Label>
                            <div className="mt-2 grid grid-cols-[120px_1fr] gap-3 items-start">
                                <div className="aspect-[16/10] rounded-sm border border-avx-line bg-avx-bg overflow-hidden flex items-center justify-center">
                                    {form.image_url ? (
                                        <img src={resolveImageUrl(form.image_url)} alt="preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="label-mono text-[9px]">no image</span>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.webp,.gif"
                                        onChange={(e) => handleImageUpload(e.target.files[0])}
                                        data-testid="product-image-file"
                                        className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:text-[10px] file:font-mono file:uppercase file:tracking-[0.18em] file:bg-avx-blue file:text-avx-bg hover:file:bg-avx-blue/90 cursor-pointer text-avx-muted"
                                    />
                                    <Input
                                        className="bg-avx-bg border-avx-line h-9 font-mono text-xs"
                                        placeholder="or paste an external URL"
                                        value={form.image_url}
                                        onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                        data-testid="product-image-url"
                                    />
                                    {uploadingImage && <div className="label-mono text-avx-blue animate-pulse-soft">Uploading image…</div>}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="published">Publicado</SelectItem>
                                        <SelectItem value="draft">Borrador</SelectItem>
                                        <SelectItem value="coming_soon">Coming soon</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-mono">Current version</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.current_version} onChange={(e) => setForm({ ...form, current_version: e.target.value })} />
                            </div>
                        </div>
                        <div>
                            <Label className="label-mono flex items-center gap-2">
                                Trial days
                                <span className="normal-case tracking-normal text-[10px] text-avx-muted">(leave empty = global default 5 days)</span>
                            </Label>
                            <Input
                                type="number"
                                min="1"
                                max="365"
                                placeholder="5"
                                className="mt-2 bg-avx-bg border-avx-line h-11 font-mono max-w-[240px]"
                                value={form.trial_days ?? ""}
                                onChange={(e) => setForm({ ...form, trial_days: e.target.value })}
                                data-testid="product-trial-days"
                            />
                        </div>
                        <div>
                            <Label className="label-mono">Features (one per line)</Label>
                            <Textarea rows={3} className="mt-2 bg-avx-bg border-avx-line" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} />
                        </div>
                        <div>
                            <Label className="label-mono">Requirements (one per line)</Label>
                            <Textarea rows={2} className="mt-2 bg-avx-bg border-avx-line" value={form.system_requirements} onChange={(e) => setForm({ ...form, system_requirements: e.target.value })} />
                        </div>
                        <div>
                            <Label className="label-mono flex items-center gap-2">
                                YouTube videos
                                <span className="text-avx-muted normal-case tracking-normal text-[10px]">(one URL per line — shown on the plugin page)</span>
                            </Label>
                            <Textarea
                                rows={3}
                                className="mt-2 bg-avx-bg border-avx-line font-mono text-xs"
                                value={form.youtube_videos}
                                onChange={(e) => setForm({ ...form, youtube_videos: e.target.value })}
                                placeholder={"https://youtu.be/dQw4w9WgXcQ\nhttps://youtube.com/watch?v=..."}
                                data-testid="product-youtube-videos"
                            />
                            {(() => {
                                const raw = typeof form.youtube_videos === "string" ? form.youtube_videos.split("\n") : (form.youtube_videos || []);
                                const ids = raw.map((s) => extractYouTubeId(s)).filter(Boolean);
                                if (ids.length === 0) return null;
                                return (
                                    <div className="mt-3 grid grid-cols-4 gap-2" data-testid="product-youtube-preview">
                                        {ids.map((id) => (
                                            <div key={id} className="relative aspect-video border border-avx-blue/40 rounded-sm overflow-hidden bg-black">
                                                <img src={youtubeThumbnail(id)} alt="preview" className="w-full h-full object-cover" />
                                                <span className="absolute bottom-1 left-1 text-[9px] font-mono text-white bg-black/70 px-1 rounded-sm">{id}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                        <button type="submit" className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="product-submit-btn">
                            {editing ? "Save changes" : "Create product"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
